<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import type { CVTemplate } from '~/types';

const { t } = useI18n();

interface TemplateOption {
  id: CVTemplate;
  name: string;
  description: string;
  preview: string;
  image?: string;
  /** Short note under the name for free accounts, e.g. Basic's "Free, no watermark". */
  label?: string;
}

const props = withDefaults(defineProps<{
  modelValue: CVTemplate;
  description?: boolean;
  switchToMobile?: boolean;
  showPreview?: boolean;
  compact?: boolean;
}>(), {
  description: false,
  switchToMobile: false,
  showPreview: false,
  compact: false
});

const { modelValue, description } = toRefs(props);

const emit = defineEmits<{
  'update:modelValue': [value: CVTemplate];
}>();

// Check if user is premium
const { isPremium, isChecked } = usePremiumStatus();

const templates: TemplateOption[] = [
  {
    id: 'basic',
    name: t('cv.templates.basic.name') || 'Basic',
    description: t('cv.templates.basic.description') || 'Plain one-column layout, free with no watermark',
    preview: '📝',
    image: '/basic.png',
    label: t('cv.templates.basic.badge'),
  },
  {
    id: 'ats',
    name: t('cv.templates.ats.name') || 'ATS Pro',
    description: t('cv.templates.ats.description') || 'Designed layout that applicant tracking systems read as clean text',
    preview: '🤖',
    image: '/ats.png'
  },
  {
    id: 'classic',
    name: t('cv.templates.classic.name') || 'Classic',
    description: t('cv.templates.classic.description') || 'Traditional professional layout with clear sections',
    preview: '📄',
    image: '/classic.png'
  },
  {
    id: 'modern',
    name: t('cv.templates.modern.name') || 'Modern',
    description: t('cv.templates.modern.description') || 'Contemporary two-column design with sidebar',
    preview: '🎨',
    image: '/modern.png'
  },
  {
    id: 'minimal',
    name: t('cv.templates.minimal.name') || 'Minimal',
    description: t('cv.templates.minimal.description') || 'Clean and elegant single-column layout',
    preview: '✨',
    image: '/minimal.png'
  },
  {
    id: 'executive',
    name: t('cv.templates.executive.name') || 'Executive',
    description: t('cv.templates.executive.description') || 'Serif, one column, no photo, for senior roles',
    preview: '🎩',
    image: '/executive.png'
  },
  {
    id: 'europass',
    name: t('cv.templates.europass.name') || 'Europass',
    description: t('cv.templates.europass.description') || 'Labels and dates on the left, content on the right',
    preview: '🇪🇺',
    image: '/europass.png'
  },
  {
    id: 'creative',
    name: t('cv.templates.creative.name') || 'Creative',
    description: t('cv.templates.creative.description') || 'Bold colour header with photo, for creative roles',
    preview: '🌈',
    image: '/creative.png'
  },
];

const selectedTemplate = computed({
  get: () => modelValue.value,
  set: (value: CVTemplate) => emit('update:modelValue', value)
});

// Handle template selection with premium check
const handleTemplateSelect = (template: TemplateOption) => {
  // Allow all users to select and preview, but show watermark for non-premium
  selectedTemplate.value = template.id;

  // Show a notification for non-premium users
  if (template.id === 'business-web' && !isPremium.value) {
    // You could emit an event here to show a notification
    console.log('Non-premium user selected business-web template - will show with watermark');
  }
};
</script>

<template>
  <div :class="compact ? 'inline-flex' : 'w-full'">

    <!-- Compact inline pill strip -->
    <div
      v-if="compact"
      class="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white p-1.5 shadow-sm dark:border-gray-700 dark:bg-gray-800"
    >
      <button
        v-for="template in templates"
        :key="template.id"
        type="button"
        :title="template.description"
        class="relative flex flex-col items-center gap-1.5 rounded-lg p-2 text-xs font-semibold transition-colors"
        :class="selectedTemplate === template.id
          ? 'bg-primary/10 text-primary ring-1 ring-primary/40 dark:bg-primary/25 dark:text-white'
          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white'"
        @click="handleTemplateSelect(template)"
      >
        <div
          class="relative h-20 w-[60px] shrink-0 overflow-hidden rounded border border-gray-200 bg-white shadow-sm dark:border-gray-600"
          :class="selectedTemplate === template.id ? 'ring-2 ring-primary/50' : ''"
        >
          <NuxtImg
            v-if="template.image"
            :src="template.image"
            :alt="`${template.name} preview`"
            :width="220"
            :height="300"
            format="webp"
            :quality="90"
            class="size-full object-cover object-top"
            loading="lazy"
          />
          <div
            v-if="selectedTemplate === template.id"
            class="absolute right-1 top-1 flex size-4 items-center justify-center rounded-full bg-primary text-white shadow"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="size-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="4" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>
        <span>{{ template.name }}</span>
        <span v-if="template.label && isChecked && !isPremium" class="!text-[10px] font-medium text-accent">{{ template.label }}</span>
      </button>
    </div>

    <!-- Mobile Select (when description prop is true) -->
    <div v-if="!compact && description" class="flex md:hidden">
      <select
:value="selectedTemplate"
        class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-800 dark:text-white"
        @change="selectedTemplate = ($event.target as HTMLSelectElement).value as CVTemplate">
        <option v-for="template in templates" :key="template.id" :value="template.id">
          {{ showPreview ? '🖼️' : '📄' }} {{ template.name }}
        </option>
      </select>
    </div>

    <!-- Desktop Grid (always visible, hidden on mobile when description is true) -->
    <div v-if="!compact" :class="description ? 'hidden md:grid' : 'grid'" class="w-full gap-2 sm:grid-cols-1 md:grid-cols-3">
      <CommonButton
        v-for="template in templates"
        :key="template.id"
        class="group relative cursor-pointer rounded-lg border p-2 text-left transition-all hover:-translate-y-0.5 hover:shadow-lg"
        :class="{
          'flex flex-col': showPreview,
          'border-primary bg-primary/5 ring-1 ring-primary/30': selectedTemplate === template.id,
          'border-gray-200 hover:border-primary/40 dark:border-gray-700 dark:hover:border-primary/60': selectedTemplate !== template.id
        }"
        @click="handleTemplateSelect(template)">

        <!-- Selected indicator -->
        <div
          v-if="selectedTemplate === template.id"
          class="absolute -right-2 -top-2 z-10 flex size-5 items-center justify-center rounded-full bg-primary text-white shadow-md">
          <svg xmlns="http://www.w3.org/2000/svg" class="size-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
          </svg>
        </div>

        <div class="flex flex-row items-center gap-3">
          <!-- Thumbnail preview -->
          <div
            class="relative h-12 w-9 shrink-0 overflow-hidden rounded-sm border border-gray-200 bg-white shadow-sm transition-transform group-hover:scale-105 dark:border-gray-600"
            :class="selectedTemplate === template.id ? 'ring-1 ring-primary/40' : ''"
          >
            <img
              v-if="template.image"
              :src="template.image"
              :alt="`${template.name} template preview`"
              class="size-full object-cover object-top"
              loading="lazy"
            >
            <div v-else class="flex size-full items-center justify-center text-base">
              {{ template.preview }}
            </div>
          </div>

          <!-- Template name -->
          <div class="min-w-0 flex-1">
            <div class="!text-sm font-semibold text-gray-900 dark:text-gray-100">
              {{ template.name }}
            </div>
            <div class="line-clamp-2 text-[11px] leading-tight text-gray-500 dark:text-gray-400">
              {{ template.description }}
            </div>
          </div>
        </div>

        <div v-if="showPreview" class="mb-3">
          <img
            v-if="template.image"
            :src="template.image"
            :alt="`${template.name} template preview`"
            class="h-52 w-full rounded-md border border-gray-200 object-cover dark:border-gray-600">
        </div>
      </CommonButton>
    </div>
  </div>
</template>
